"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { 
  ArrowRight, 
  Play, 
  Sparkles, 
  Code2, 
  Zap, 
  Users, 
  Rocket,
  Star,
  TrendingUp,
  Shield,
  Globe
} from "lucide-react"

const demoTexts = [
  "Create a stunning event management platform...",
  "Build an AI-powered ticket booking system...",
  "Design a real-time analytics dashboard...",
  "Generate a community engagement app..."
]

const floatingIcons = [
  { Icon: Code2, delay: 0, duration: 3 },
  { Icon: Sparkles, delay: 0.5, duration: 4 },
  { Icon: Zap, delay: 1, duration: 3.5 },
  { Icon: Rocket, delay: 1.5, duration: 4.5 },
]

export function HeroSection() {
  const [currentText, setCurrentText] = useState("")
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTyping, setIsTyping] = useState(true)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isTyping) {
      const fullText = demoTexts[currentIndex]
      if (currentText.length < fullText.length) {
        timeout = setTimeout(() => {
          setCurrentText(fullText.slice(0, currentText.length + 1))
        }, 50)
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false)
        }, 2000)
      }
    } else {
      if (currentText.length > 0) {
        timeout = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1))
        }, 30)
      } else {
        setCurrentIndex((prev) => (prev + 1) % demoTexts.length)
        setIsTyping(true)
      }
    }

    return () => clearTimeout(timeout)
  }, [currentText, currentIndex, isTyping])

  if (!mounted) return null

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-background">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.03) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
          style={{
            background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
            animationDuration: '4s'
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
          style={{
            background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
            animationDuration: '6s',
            animationDelay: '1s'
          }}
        />
      </div>

      {/* Floating Icons */}
      {floatingIcons.map(({ Icon, delay, duration }, idx) => (
        <div
          key={idx}
          className="absolute opacity-10 dark:opacity-5 pointer-events-none"
          style={{
            top: `${20 + idx * 15}%`,
            left: `${10 + idx * 20}%`,
            animation: `float ${duration}s ease-in-out infinite`,
            animationDelay: `${delay}s`
          }}
        >
          <Icon className="w-12 h-12" style={{ color: `rgb(var(--theme-primary))` }} />
        </div>
      ))}

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        {/* Top Badge */}
        <div className="flex justify-center mb-8 animate-in fade-in slide-in-from-top duration-700">
          <Badge 
            variant="secondary" 
            className="glass-card border px-4 py-2 text-sm"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" style={{ color: `rgb(var(--theme-primary))` }} />
            <span className="text-theme-primary font-semibold">AI-Powered Event Platform</span>
            <Star className="ml-2 h-3.5 w-3.5" style={{ color: `rgb(var(--theme-accent))` }} />
          </Badge>
        </div>

        {/* Main Heading */}
        <h1 className="mx-auto max-w-5xl text-4xl font-bold tracking-tight text-foreground sm:text-6xl lg:text-7xl mb-6 animate-in fade-in slide-in-from-bottom duration-700 delay-100">
          Transform Your Vision Into{" "}
          <span 
            className="relative inline-block"
            style={{
              background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Reality
            <svg
              className="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 5.5C50 2.5 150 2.5 199 5.5"
                stroke={`rgb(var(--theme-accent))`}
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </h1>

        {/* Subtitle */}
        <p className="mx-auto mt-6 max-w-3xl text-lg text-muted-foreground sm:text-xl leading-relaxed animate-in fade-in slide-in-from-bottom duration-700 delay-200">
          Swaraah's AI understands your requirements and generates{" "}
          <span className="font-semibold text-foreground">production-ready event platforms</span> with 
          drag-and-drop editing, real-time analytics, and seamless deployment.
        </p>

        {/* Demo Terminal */}
        <div className="mx-auto mt-16 max-w-3xl animate-in fade-in slide-in-from-bottom duration-700 delay-300">
          <div className="relative group">
            {/* Glow Effect */}
            <div 
              className="absolute -inset-0.5 rounded-2xl opacity-30 group-hover:opacity-50 blur-xl transition-opacity duration-500"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            />
            
            {/* Terminal */}
            <div className="relative glass-card rounded-2xl border backdrop-blur-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex space-x-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Zap className="h-3 w-3" style={{ color: `rgb(var(--theme-primary))` }} />
                  <span>AI Terminal</span>
                </div>
              </div>
              
              <div className="font-mono text-left text-sm sm:text-base">
                <span className="text-muted-foreground">$ swaraah generate</span>
                <div className="mt-2 min-h-[60px]">
                  <span className="gradient-text font-medium">
                    {currentText}
                    <span className="animate-pulse">|</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6 animate-in fade-in slide-in-from-bottom duration-700 delay-400">
          <Link to="/signup">
            <Button 
              size="lg" 
              className="group bg-gradient-to-r text-white shadow-lg hover:shadow-2xl transition-all duration-300 text-lg px-8 py-6 rounded-xl"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
              }}
            >
              <Rocket className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              Start Building Free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          
          <Button 
            variant="outline" 
            size="lg" 
            className="group glass-card border text-lg px-8 py-6 rounded-xl hover:shadow-lg transition-all duration-300"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
            }}
          >
            <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Watch Demo
          </Button>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom duration-700 delay-500">
          {[
            { icon: Shield, value: "Enterprise", label: "Security Grade" },
            { icon: Globe, value: "150+", label: "Countries Worldwide" },
            { icon: TrendingUp, value: "99.9%", label: "Uptime Guarantee" }
          ].map((item, idx) => (
            <div 
              key={idx} 
              className="flex flex-col items-center gap-3 p-6 glass-card rounded-xl border hover:shadow-lg transition-all duration-300 group"
              style={{
                borderColor: `rgba(var(--theme-primary), 0.2)`,
              }}
            >
              <div 
                className="p-3 rounded-lg"
                style={{
                  background: `rgba(var(--theme-primary), 0.1)`
                }}
              >
                <item.icon 
                  className="h-6 w-6 group-hover:scale-110 transition-transform" 
                  style={{ color: `rgb(var(--theme-primary))` }} 
                />
              </div>
              <div className="text-2xl font-bold gradient-text">{item.value}</div>
              <div className="text-sm text-muted-foreground">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-24 grid grid-cols-1 gap-8 sm:grid-cols-3 animate-in fade-in slide-in-from-bottom duration-700 delay-600">
          {[
            { value: "2.3M+", label: "Events Created" },
            { value: "99.2%", label: "User Satisfaction" },
            { value: "<30s", label: "Avg. Build Time" }
          ].map((stat, index) => (
            <div key={index} className="group cursor-pointer">
              <div 
                className="text-4xl sm:text-5xl font-bold mb-2 transition-transform group-hover:scale-110 duration-300"
                style={{
                  background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }
      `}</style>
    </section>
  )
}
